/**
 * The header Guide button, and everything it owns — decision G13.
 *
 * Sits next to "Report a problem" and appears only on a screen that has guidance: the
 * current route is matched against `/api/gms/screens/`, which for a reader lists only
 * screens with at least one published guide. An author also gets screens with none, so
 * there is somewhere to start the first one.
 *
 * Wrapped in an error boundary because `docs/gms/ARCHITECTURE.md` §8 requires that a GMS
 * failure never impairs the page underneath. If anything in here throws, the button
 * disappears and the page carries on — which is the correct outcome for a help feature
 * and the wrong one for almost anything else.
 *
 * A page that holds state a walkthrough needs publishes it with `GuidePageContext`;
 * everything else is resolved from the route.
 */

import {
  Component,
  type ErrorInfo,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BookOpen } from 'lucide-react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { usePublishedGuidePageContext } from '../guide-page-context-store';
import { GUIDE_PARAM, GUIDE_VERSION_PARAM, resolveScreen } from '../guide-screens';
import {
  useGmsAccess,
  useGmsScreens,
  useWalkthrough,
  useWalkthroughActions,
} from '../hooks/use-gms';
import type { PassReason, TopicSummary } from '../types';
import { GMS_UI_ATTR } from '../recorder/locator';
import { requestRecording } from '../recorder/recorder-store';
import { GuideRecorder } from './guide-recorder';
import { HelpDrawer } from './help-drawer';
import { WalkthroughOverlay } from './walkthrough-overlay';
import { WalkthroughStartPanel } from './walkthrough-start-panel';
import '../gms.css';

/** Keeps a GMS failure inside GMS. */
class GmsBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Logged, not surfaced. The person using the page did not ask for help with help.
    console.error('GMS failed and was disabled for this page', error, info);
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

function GuideButtonInner() {
  const { data: access } = useGmsAccess();
  const inRollout = Boolean(access?.gms_enabled && access?.can_view);
  // Authors get the button on every screen: it is where they record a guide (G19).
  const isAuthor = Boolean(inRollout && access?.can_author);
  const { data: screens } = useGmsScreens(inRollout);
  const pageContext = usePublishedGuidePageContext();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // A page's own context wins over route matching; either way the screen must be one
  // the server listed, or there is nothing to offer here.
  const screen = useMemo(() => {
    if (!screens) return null;
    if (pageContext) {
      return (
        screens.find(
          (entry) =>
            entry.tool_key === pageContext.toolKey &&
            (!pageContext.locationKey || entry.location_key === pageContext.locationKey)
        ) ?? null
      );
    }
    return resolveScreen(screens, location.pathname);
  }, [screens, pageContext, location.pathname]);

  const toolKey = screen?.tool_key ?? null;
  const locationKey = screen?.location_key;
  const contextIds = useMemo(() => pageContext?.contextIds ?? {}, [pageContext]);
  const hasUnsavedToolData = pageContext?.hasUnsavedToolData ?? false;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [chosen, setChosen] = useState<TopicSummary | null>(null);
  const [currentStepKey, setCurrentStepKey] = useState('');
  const [isPreview, setIsPreview] = useState(false);
  // Set by Review & Edit's "Try it": the draft version an author is checking (G18).
  const [draftVersion, setDraftVersion] = useState<number | undefined>(undefined);
  /**
   * The walkthrough in progress, pinned when it starts. A guide may cross screens (a
   * recorded step on /leads, the next on /leads/:leadId), so once it runs it is no
   * longer tied to whatever screen the header resolves now: route changes neither
   * reset it nor, on a screen with no guide, hide it.
   */
  const [run, setRun] = useState<{ toolKey: string; topicKey: string; version?: number } | null>(
    null
  );
  const running = run !== null;
  const runRef = useRef(run);
  runRef.current = run;

  const activeTool = run?.toolKey ?? toolKey;
  const topicKey = run?.topicKey ?? chosen?.topic_key ?? null;
  const { data: freshDetail } = useWalkthrough(
    activeTool,
    topicKey,
    contextIds,
    run ? run.version : draftVersion
  );
  // While running, a refetch under a new key (contextIds change with the page) briefly
  // has no data; keep showing the last steps so the overlay is not torn down mid-step.
  const lastDetail = useRef(freshDetail);
  if (freshDetail) lastDetail.current = freshDetail;
  const detail = freshDetail ?? (running ? lastDetail.current : undefined);
  const actions = useWalkthroughActions(activeTool ?? '', topicKey ?? '');

  // Leaving the screen closes the drawer and an unstarted pre-start panel. A running
  // walkthrough carries on across screens (it ends only by End, completion or Escape).
  useEffect(() => {
    setDrawerOpen(false);
    if (runRef.current) return;
    setChosen(null);
    setDraftVersion(undefined);
  }, [toolKey, locationKey]);

  // "Guide me" on a reader page lands here with ?guide=<topic>. Open that walkthrough's
  // pre-start panel — never start it — and drop the parameter so a reload does not
  // reopen it.
  const requestedTopic = searchParams.get(GUIDE_PARAM);
  const requestedVersion = Number(searchParams.get(GUIDE_VERSION_PARAM)) || undefined;
  useEffect(() => {
    if (!requestedTopic || !toolKey || runRef.current) return;
    // Only an author may ask for a specific version; the server refuses anyone else.
    setDraftVersion(access?.can_author ? requestedVersion : undefined);
    setChosen({
      topic_key: requestedTopic,
      tool_key: toolKey,
      topic_type: 'walkthrough',
      title: '',
      summary: '',
    });
    const next = new URLSearchParams(searchParams);
    next.delete(GUIDE_PARAM);
    next.delete(GUIDE_VERSION_PARAM);
    setSearchParams(next, { replace: true });
  }, [
    requestedTopic,
    requestedVersion,
    toolKey,
    access?.can_author,
    searchParams,
    setSearchParams,
  ]);

  const onStart = useCallback(
    (requestedPreview: boolean) => {
      // A draft can only ever be previewed: it is not live, so it earns nothing.
      const preview = requestedPreview || draftVersion !== undefined;
      actions.start.mutate(
        { contextIds, isPreview: preview, version: draftVersion },
        {
          onSuccess: (progress) => {
            if (!toolKey || !chosen) return;
            setIsPreview(preview);
            setCurrentStepKey(progress.current_step_key);
            setRun({ toolKey, topicKey: chosen.topic_key, version: draftVersion });
            setDrawerOpen(false);
          },
        }
      );
    },
    [actions.start, contextIds, draftVersion, toolKey, chosen]
  );

  const onResume = useCallback(() => {
    if (!detail?.progress || !toolKey || !chosen) return;
    setIsPreview(detail.progress.is_preview);
    setCurrentStepKey(detail.progress.current_step_key);
    setRun({ toolKey, topicKey: chosen.topic_key, version: draftVersion });
    setDrawerOpen(false);
  }, [detail, toolKey, chosen, draftVersion]);

  /** The walkthrough is over, however it ended: forget it. */
  const finish = useCallback(() => {
    setRun(null);
    setChosen(null);
    setDraftVersion(undefined);
  }, []);

  const onEnd = useCallback(() => {
    actions.end.mutate('');
    finish();
  }, [actions.end, finish]);

  const onComplete = useCallback(
    (signal: string) => {
      actions.complete.mutate(signal, { onSuccess: finish });
    },
    [actions.complete, finish]
  );

  const onAdvance = useCallback((nextStepKey: string) => setCurrentStepKey(nextStepKey), []);

  // Next / Skip (G23). The server moves on — or, on the final step, finishes the
  // walkthrough itself — so there is no separate complete call here.
  const onPass = useCallback(
    async (stepKey: string, reason: PassReason) => {
      const result = await actions.pass.mutateAsync({ stepKey, reason, contextIds });
      if (result.finished) finish();
      else setCurrentStepKey(result.current_step_key);
    },
    [actions.pass, contextIds, finish]
  );

  if (!inRollout) return null;
  // The button shows where there is guidance (any screen, for an author); a running
  // walkthrough keeps its overlay even on a screen that has none.
  const showButton = Boolean(screen) || isAuthor;
  if (!showButton && !running) return null;

  return (
    <>
      {isAuthor ? <GuideRecorder screenName={screen?.display_name} /> : null}
      {showButton ? (
        <button
          type="button"
          className="header__report-button"
          onClick={() => setDrawerOpen(true)}
          aria-label={screen ? `Guide for ${screen.display_name}` : 'Guide'}
          title="Guide for this screen"
          {...{ [GMS_UI_ATTR]: '' }}
        >
          <BookOpen size={18} />
          <span className="header__report-label">Guide</span>
        </button>
      ) : null}

      <HelpDrawer
        toolKey={toolKey}
        locationKey={locationKey}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        // The start panel replaces the list. Leaving the drawer open would portal it
        // over the panel, hiding Start.
        onChooseWalkthrough={(topic) => {
          setChosen(topic);
          setDrawerOpen(false);
        }}
        showAuthorEmptyNote={isAuthor && (!screen || screen.published_guide_count === 0)}
        onRecord={
          isAuthor
            ? () => {
                setDrawerOpen(false);
                requestRecording();
              }
            : undefined
        }
      />

      {/* The pre-start panel lives in the drawer once a walkthrough is chosen. */}
      {chosen && detail && !running ? (
        <div className="wb-gms-drawer" {...{ [GMS_UI_ATTR]: '' }}>
          <header className="wb-gms-drawer__header">
            <h2 className="wb-gms-drawer__title">{detail.title}</h2>
            <button
              type="button"
              onClick={() => {
                setChosen(null);
                setDraftVersion(undefined);
              }}
              aria-label="Close"
            >
              ✕
            </button>
          </header>
          <div className="wb-gms-drawer__body">
            <WalkthroughStartPanel
              detail={detail}
              canPreview={Boolean(access?.can_author)}
              previewOnly={detail.draft_preview}
              starting={actions.start.isPending}
              error={
                actions.start.error instanceof Error
                  ? actions.start.error.message
                  : undefined
              }
              onStart={onStart}
              onResume={onResume}
            />
          </div>
        </div>
      ) : null}

      {run && detail ? (
        <WalkthroughOverlay
          toolKey={run.toolKey}
          screens={screens ?? []}
          topicKey={run.topicKey}
          title={detail.title}
          // Step state is served with the detail payload; the overlay renders it and
          // never decides for itself whether a step is satisfied.
          steps={detail.steps}
          currentStepKey={currentStepKey}
          isPreview={isPreview}
          hasUnsavedToolData={hasUnsavedToolData}
          onAdvance={onAdvance}
          onEnd={onEnd}
          onComplete={onComplete}
          onPass={onPass}
        />
      ) : null}
    </>
  );
}

/** The header Guide button, with GMS failures contained. */
export function GuideButton() {
  return (
    <GmsBoundary>
      <GuideButtonInner />
    </GmsBoundary>
  );
}
